import { ComponentProps, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '@sib/ui';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './BaseFormComponents';
import { FormComponentBaseProps } from './types/form-base-props';

export type FormCheckboxProps = Omit<
  FormComponentBaseProps & ComponentProps<typeof Checkbox> & {},
  'onChange'
> & {
  softDisabled?: boolean;
};

export const FormCheckbox = ({
  description,
  descriptionProps,
  label,
  labelProps,
  hideError,
  defaultValue,
  onCheckedChange,
  name,
  disabled,
  softDisabled,
  ...props
}: FormCheckboxProps) => {
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  return (
    <FormField
      shouldUnregister={false}
      disabled={disabled}
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field, fieldState }) => (
        <FormItem className="flex flex-row items-start rounded-md">
          <FormControl>
            <Checkbox
              {...field}
              {...props}
              disabled={disabled || isSubmitting}
              checked={field.value}
              onCheckedChange={value => {
                onCheckedChange?.(value);
                if (softDisabled) return;
                field.onChange(value);
              }}
              id={`checkbox-${name}`}
            />
          </FormControl>
          <div className="self-center leading-4">
            <FormLabel
              className="cursor-pointer pl-2"
              htmlFor={`checkbox-${name}`}
              {...labelProps}
            >
              {label}
            </FormLabel>
            <FormDescription {...descriptionProps}>
              {description}
            </FormDescription>
            {!hideError && (
              <FormMessage>{fieldState.error?.message}</FormMessage>
            )}
          </div>
        </FormItem>
      )}
    />
  );
};

export default memo(FormCheckbox);
